/*!
 * Copyright (c) 2026 Elevation Technologies Pte Ltd.
 * Developer: filimoni@elevationtech.com.fj
 */
import React, { useMemo, useState } from 'react';

export default function TabsBlock({ items = [] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const tabPrefix = useMemo(() => `fsf-tabs-${Math.random().toString(36).slice(2)}`, []);

  if (!items.length) {
    return null;
  }

  const activateTab = (index, focusPanel = false) => {
    setActiveIndex(index);

    window.requestAnimationFrame(() => {
      const target = document.getElementById(`${tabPrefix}-${focusPanel ? 'panel' : 'tab'}-${index}`);

      if (!target) {
        return;
      }

      try {
        target.focus({ preventScroll: focusPanel });
      } catch (error) {
        target.focus();
      }

      if (focusPanel) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  const handleTabKeyDown = (event, index) => {
    let nextIndex = index;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (index + 1) % items.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (index - 1 + items.length) % items.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = items.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    activateTab(nextIndex);
  };

  return (
    <section className="fsf-tabs">
      <div className="fsf-tabs__list" role="tablist" aria-label="Service details" aria-orientation="vertical">
        {items.map((item, index) => (
          <button
            key={`${item.title}-${index}`}
            id={`${tabPrefix}-tab-${index}`}
            type="button"
            role="tab"
            aria-selected={index === activeIndex}
            aria-controls={`${tabPrefix}-panel-${index}`}
            tabIndex={index === activeIndex ? 0 : -1}
            className={index === activeIndex ? 'is-active' : ''}
            onClick={() => setActiveIndex(index)}
            onKeyDown={(event) => handleTabKeyDown(event, index)}
          >
            {item.title}
          </button>
        ))}
      </div>
      {items.map((item, index) => {
        const previousIndex = (index - 1 + items.length) % items.length;
        const nextIndex = (index + 1) % items.length;

        return (
          <div
            key={`${item.title}-panel-${index}`}
            id={`${tabPrefix}-panel-${index}`}
            className="fsf-tabs__panel"
            role="tabpanel"
            aria-labelledby={`${tabPrefix}-tab-${index}`}
            tabIndex="0"
            hidden={index !== activeIndex}
          >
            <div className="fsf-copy" dangerouslySetInnerHTML={{ __html: item.html }} />
            {items.length > 1 ? (
              <div className="fsf-tabs__pager" aria-label="Service tab navigation">
                <button
                  type="button"
                  className="fsf-tabs__pager-button"
                  aria-label={`Previous tab: ${items[previousIndex].title}`}
                  onClick={() => activateTab(previousIndex, true)}
                >
                  <span>Previous</span>
                  <strong>{items[previousIndex].title}</strong>
                </button>
                <button
                  type="button"
                  className="fsf-tabs__pager-button fsf-tabs__pager-button--next"
                  aria-label={`Next tab: ${items[nextIndex].title}`}
                  onClick={() => activateTab(nextIndex, true)}
                >
                  <span>Next</span>
                  <strong>{items[nextIndex].title}</strong>
                </button>
              </div>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}
